import type { Ref, ComputedRef } from 'vue'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'
import { TasksKanbanStage } from '@brighterly/lib-core-types/tasks-kanban/enums'

import dayjs from 'dayjs'

import { useTasksKanbanStore } from './store'

interface TaskCreationPickerDeadlineReturn {
  refDate: Ref<string | null>
  refStartTime: Ref<{ hours: number, minutes: number }>
  refMinDate: ComputedRef<string>
  refStageAge: ComputedRef<string>
  refIsError: ComputedRef<boolean>
  handleChange: (date: string | null) => void
}

export const useTaskCreationPickerDeadline = (): TaskCreationPickerDeadlineReturn => {
  const tasksKanbanStore = useTasksKanbanStore()

  const getInitialValue = (): string | null => {
    const deadline = tasksKanbanStore.getDeadline
    return deadline ? dayjs(deadline, DateFormat.Default).format() : null
  }

  const refDate = ref<string | null>(getInitialValue())
  const refStartTime = ref({ hours: 14, minutes: 0 })

  watch(
    () => tasksKanbanStore.getDeadline,
    (deadline) => {
      refDate.value = deadline ? dayjs(deadline, DateFormat.Default).format() : null
    },
  )

  const refMinDate = computed<string>(() => {
    return dayjs().startOf('day').format(DateFormat.Default)
  })

  const refIsError = computed<boolean>(() =>
    tasksKanbanStore.getSubmitted &&
    !tasksKanbanStore.getDeadline &&
    tasksKanbanStore.getStage !== TasksKanbanStage.Resolved &&
    tasksKanbanStore.getStage !== TasksKanbanStage.Cancelled,
  )

  const refStageAge = computed<string>(() => {
    const createdAt = tasksKanbanStore.getCreatedAt

    if (!createdAt) return ''

    const days = dayjs().diff(dayjs(createdAt, DateFormat.Default), 'day')

    if (days <= 0) return 'Today'
    return `${days} days`
  })

  const handleChange = (date: string | null): void => {
    const value = (!!date && date !== 'Invalid Date')
      ? dayjs(date).format(DateFormat.Default)
      : null

    tasksKanbanStore.setDeadline(value)
  }

  return {
    refDate,
    refStartTime,
    refMinDate,
    refStageAge,
    refIsError,
    handleChange,
  }
}
